import { MaterialCommunityIcons } from '@expo/vector-icons';
import { DateTypeFilter, getLocalTime } from '@gripp/shared-logic';
import { Colors } from 'libs/shared-ui/src/themes';
import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import {
  KeyboardTypeOptions,
  Platform,
  Pressable,
  StyleProp,
  Text,
  TextProps,
  View,
  ViewStyle,
} from 'react-native';
import { useOpenedState } from '../../../hooks';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { StyledTextInput } from '../controlledTextInput';
import { datePickerStyle as styles } from './datePickerStyle';
import { DatePickerWheel } from './datePickerWheel';
import { DateRangePickerSelector } from './dateRangePickerSelector';
import { DateRangeSelectorProps } from './dateRangeSelector';
import { WebDateTimePicker } from './webDateTimePicker';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

const cropCircleStyles = themedStyleSheet((role) => ({
  container: {
    borderRadius: 4,
    backgroundColor: role.surfaceHover,
    borderWidth: 0,
    minHeight: 48,
    height: 48,
    flex: 1, // Share space equally when in a row (e.g. blackout dates)
    padding: 0, // Override dateRangeFieldContainer's padding: 8 - text and icon have their own 16px
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: 'center',
    flexDirection: 'column',
  },
  inputContainer: {
    paddingHorizontal: 0,
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between', // Keep text left, calendar icon right
  },
  inputText: {
    ...tokens.typography.body.small,
    color: role.textStrong,
    paddingLeft: 16, // Exactly 16px left padding for text (design)
    paddingRight: 0,
    marginRight: 0,
    flex: 1,
    borderRadius: 0,
    backgroundColor: 'transparent',
    ...Platform.select({
      ios: {
        lineHeight: undefined,
      },
    }),
  },
  calendarIcon: {
    margin: 0,
    marginLeft: 0, // Override any marginLeft from old styles
    marginRight: 0,
    padding: 0,
    paddingRight: 16, // Exactly 16px right padding for icon (design)
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

type DatePickerSelectorProps = DateRangeSelectorProps & {
  name: string;
  placeholder?: string;
  textArea?: boolean;
  innerRef?: any;
  keyboardType?: KeyboardTypeOptions;
  height?: number;
  label?: string;
  isStartDate?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  mode?: 'date' | 'time' | 'datetime';
  maxYears?: number;
  calendarIconStyle?: StyleProp<ViewStyle>;
  /** Optional style for the native date picker modal container (e.g. background). Applied on iOS. */
  pickerContainerStyleIOS?: StyleProp<ViewStyle>;
  /** Optional style for the native date picker modal content. Applied on iOS. */
  modalStyleIOS?: StyleProp<ViewStyle>;
  /** Optional style for the label above the date field. */
  labelStyle?: TextProps['style'];
  /** Optional color for the calendar/clock icon. */
  calendarIconColor?: string;
  /** Optional style for the iOS modal confirm button container. */
  confirmButtonStyleIOS?: StyleProp<ViewStyle>;
  /** When true, disables the input and prevents the calendar dropdown from opening. */
  disabled?: boolean;
  /**
   * Optional solid text color for the disabled value. When set, the field opts
   * out of the default 0.4-opacity dimming so read-only displays (e.g. the
   * created-date field) can render a legible, consistent grey instead of a
   * washed-out one.
   */
  disabledTextColor?: string;
  /** When true, hides the calendar icon and adjusts clear button position. */
  hideCalendarIcon?: boolean;
};

export const DatePickerSelector = (props: DatePickerSelectorProps) => {
  const isWeb = Platform.OS === 'web';
  const datePickerStartDateRef = useRef<View>(null!);
  const {
    onOpen: onDateRangePickerSelectorOpen,
    onClose: onDateRangePickerSelectorClose,
    isOpened: isDateRangePickerSelectorOpened,
  } = useOpenedState();

  const handleDateRangeSelector = () => {
    if (props.disabled) return;
    props.setValue('dateType', DateTypeFilter.CUSTOMDATE);
    onDateRangePickerSelectorOpen();
  };

  if (props.mode === 'time' && isWeb) {
    return (
      <WebDateTimePicker
        control={props.control}
        setValue={props.setValue}
        isStartDate={props.isStartDate}
        name={props.name}
        label={props.label}
        disabled={props.disabled}
        disabledTextColor={props.disabledTextColor}
        containerStyle={props.containerStyle}
      />
    );
  }

  return (
    <View
      style={[
        styles.dateRangeFieldContainer,
        isDateRangePickerSelectorOpened &&
          styles.dateRangeFieldContainerActiveRedesign,
        cropCircleStyles.container,
        isDateRangePickerSelectorOpened && styles.dateRangeFieldContainerActive,
        props.containerStyle,
        isWeb && { width: 'auto', minWidth: 0 },
      ]}
    >
      <Controller
        control={props.control}
        name={props.name}
        render={({ field: { onChange, onBlur, value } }) => {
          const valueFormat =
            props.mode === 'time' && value ? getLocalTime(value) : value;
          const hasValue = !!valueFormat;
          const isOpen = isDateRangePickerSelectorOpened;

          const handleClear = () => {
            onChange('');
          };

          return (
            <>
              {props.label && (
                <View style={[styles.labelContainer]}>
                  <Text
                    style={[styles.label, props.labelStyle]}
                    numberOfLines={1}
                  >
                    {props.label}
                  </Text>
                </View>
              )}
              <Pressable
                onPress={() => {
                  if (!isOpen && !props.disabled) {
                    handleDateRangeSelector();
                  }
                }}
                style={[
                  styles.inputTouchArea,
                  props.disabled &&
                    !props.disabledTextColor && { opacity: 0.4 },
                ]}
                disabled={props.disabled}
              >
                <View style={cropCircleStyles.inputContainer}>
                  <StyledTextInput
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={valueFormat ?? ''}
                    placeholder={props.placeholder}
                    editable={false}
                    textArea={props.textArea}
                    innerRef={props.innerRef}
                    keyboardType={props.keyboardType}
                    height={props.height ?? 48}
                    backgroundColor={'transparent'}
                    textColor={
                      props.disabled ? props.disabledTextColor : undefined
                    }
                    inputInnerStyle={[
                      cropCircleStyles.inputText,
                      props.disabled &&
                        props.disabledTextColor && {
                          color: props.disabledTextColor,
                        },
                    ]}
                    pointerEvents="none"
                    disableInnerStyle
                  />
                  {!props.hideCalendarIcon && (
                    <View
                      style={[
                        cropCircleStyles.calendarIcon,
                        props.calendarIconStyle,
                      ]}
                    >
                      <Icon
                        name={props.mode === 'time' ? 'Clock A' : 'Calendar'}
                        size={18}
                        // The fallback used to be a hardcoded black, which
                        // defeated the icon's own themed default and left the
                        // glyph invisible on a dark field. Crop Circle icons
                        // take a `var()` safely (see `useRoleHex.ts`).
                        color={
                          props.calendarIconColor ?? Colors.role.textStrong
                        }
                      />
                    </View>
                  )}
                </View>
              </Pressable>
              {(isOpen || hasValue || !props.disabled) && (
                <View
                  style={[
                    styles.datePickerIcons,
                    props.hideCalendarIcon && { right: 16 },
                  ]}
                >
                  {isOpen && (
                    <MaterialCommunityIcons
                      name="chevron-up"
                      size={20}
                      color={Colors.role.textSecondary}
                    />
                  )}
                  {hasValue && !props.disabled && (
                    <Pressable onPress={handleClear} hitSlop={8}>
                      <MaterialCommunityIcons
                        name="close"
                        size={16}
                        color={Colors.role.textSecondary}
                      />
                    </Pressable>
                  )}
                </View>
              )}
              <View ref={datePickerStartDateRef}>
                {isWeb ? (
                  <DateRangePickerSelector
                    control={props.control}
                    setValue={props.setValue}
                    onDateSelected={onChange}
                    anchorRef={datePickerStartDateRef}
                    isVisible={isDateRangePickerSelectorOpened}
                    onClose={onDateRangePickerSelectorClose}
                    value={value}
                    isStartDate={props.isStartDate}
                    mode={props.mode}
                    maxYears={props.maxYears}
                    minDate={props.minDate}
                  />
                ) : (
                  <DatePickerWheel
                    control={props.control}
                    setValue={props.setValue}
                    onDateSelected={onChange}
                    isVisible={isDateRangePickerSelectorOpened}
                    onClose={onDateRangePickerSelectorClose}
                    isStartDate={props.isStartDate}
                    mode={props.mode}
                    name={props.name}
                    minDate={props.minDate}
                    pickerContainerStyleIOS={
                      props.pickerContainerStyleIOS as any
                    }
                    modalStyleIOS={props.modalStyleIOS as any}
                    confirmButtonStyleIOS={props.confirmButtonStyleIOS as any}
                  />
                )}
              </View>
            </>
          );
        }}
      />
    </View>
  );
};
